import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Upload, PartyPopper, X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { API, BOOKING_SERVICES, TIME_OPTIONS, BUSINESS } from "../data/site";

const SERVICE_TESTIDS = {
    "Lawn Care": "booking-form-service-lawn-care",
    "Gutter Cleaning": "booking-form-service-gutter-cleaning",
    "Window Cleaning": "booking-form-service-window-cleaning",
    "Junk Removal": "booking-form-service-junk-removal",
};

const inputCls = "w-full rounded-xl border border-forest/20 bg-bone-card px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest focus:ring-2 focus:ring-forest/15";

export default function BookNow() {
    const [params] = useSearchParams();
    const preselected = useMemo(
        () => (params.get("services") || "").split(",").map((s) => s.trim()).filter((s) => BOOKING_SERVICES.includes(s)),
        [params]
    );
    const [reqType, setReqType] = useState(params.get("type") === "quote" ? "quote" : "booking");
    const isQuote = reqType === "quote";
    const [form, setForm] = useState({
        full_name: "", phone: "", email: "", address: "",
        preferred_date: "", preferred_time: TIME_OPTIONS[0], notes: "", budget: "", frequency: "One-off",
    });
    const [services, setServices] = useState(preselected);
    const [photo, setPhoto] = useState(null);
    const [serviceError, setServiceError] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const toggleService = (s) => {
        setServiceError(false);
        setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const onPhoto = (e) => {
        const file = e.target.files?.[0];
        if (!file) return setPhoto(null);
        if (file.size > 4_500_000) {
            toast.error("Photo is too large — please choose one under 4MB.");
            e.target.value = "";
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setPhoto({ name: file.name, data: reader.result });
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (services.length === 0) {
            setServiceError(true);
            toast.error("Please select at least one service.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/bookings`, {
                ...form,
                services,
                request_type: reqType,
                photo_name: photo?.name || null,
                photo_data: photo?.data || null,
            });
            setDone(true);
            window.scrollTo(0, 0);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong — please try again or call us.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <main data-testid="book-now-page" className="pt-16">
                <section className="mx-auto max-w-2xl px-6 py-24 text-center lg:py-32">
                    <Reveal>
                        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-forest text-bone">
                            <PartyPopper size={26} />
                        </span>
                        <h1 className="mt-8 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                            {isQuote ? "Quote request received" : "Booking request received"}
                        </h1>
                        <p data-testid="booking-success-message" className="mt-5 rounded-2xl border border-forest/15 bg-bone-subtle p-6 text-base leading-relaxed text-ink">
                            {isQuote
                                ? "Thanks for your quote request! We'll contact you shortly with a free, no-obligation quote."
                                : "Thanks for your booking request! We'll contact you to confirm the details and arrange a suitable time."}
                        </p>
                        <p className="mt-4 text-sm text-moss">
                            Need it sooner? Call us on{" "}
                            <a href={BUSINESS.phoneHref} className="font-bold text-forest">{BUSINESS.phone}</a>.
                        </p>
                        <Link to="/" data-testid="booking-success-home-button"
                            className="mt-8 inline-block rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                            Back to Home
                        </Link>
                    </Reveal>
                </section>
            </main>
        );
    }

    return (
        <main data-testid="book-now-page" className="pt-16">
            <Seo title="Book Lawn Care & More Online | Lawn Busters Adelaide"
                description="Book lawn care, gutter cleaning, window cleaning or junk removal in Adelaide — combine up to 3 services in one visit, or request a free quote." />
            <section className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
                <p className="eyebrow text-forest-leaf">{isQuote ? "Free quote" : "Book a service"}</p>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    <KineticLines key={reqType} lines={isQuote ? ["Get A Free", "Quote"] : ["Book Your", "Service"]} />
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-moss">
                    {isQuote
                        ? "Tell us what you need and we'll come back to you with a free, no-obligation quote. Pick one service or combine several — the choice is yours."
                        : "Select one, two or three services in a single booking — no need to fill out multiple forms. We'll contact you to confirm the details and arrange a suitable time."}
                </p>

                <form onSubmit={submit} data-testid="booking-form" className="mt-12 space-y-8">
                    <div className="grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => setReqType("booking")} data-testid="booking-type-booking" aria-pressed={!isQuote}
                            className={`rounded-2xl border px-5 py-4 text-sm font-bold transition-colors ${
                                !isQuote ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                            }`}>
                            Book a Service
                        </button>
                        <button type="button" onClick={() => setReqType("quote")} data-testid="booking-type-quote" aria-pressed={isQuote}
                            className={`rounded-2xl border px-5 py-4 text-sm font-bold transition-colors ${
                                isQuote ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                            }`}>
                            Get a Free Quote
                        </button>
                    </div>
                    <div>
                        <p className="eyebrow mb-4 text-forest">Services required</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {BOOKING_SERVICES.map((s) => {
                                const active = services.includes(s);
                                return (
                                    <button type="button" key={s} onClick={() => toggleService(s)}
                                        data-testid={SERVICE_TESTIDS[s]} aria-pressed={active}
                                        className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left text-sm font-bold transition-colors ${
                                            active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                                        }`}>
                                        {s}
                                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${active ? "border-bone bg-bone text-forest" : "border-forest/30 text-transparent"}`}>
                                            <Check size={13} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {serviceError && (
                            <p data-testid="booking-form-services-error" className="mt-3 text-sm font-semibold text-clay">
                                Please select at least one service.
                            </p>
                        )}
                        <p className="mt-3 text-xs text-moss">Pick any combination — for example Lawn Care + Gutter Cleaning + Junk Removal.</p>
                        <p className="mt-2 text-xs text-moss">
                            Curious about cost?{" "}
                            <Link to="/pricing" data-testid="booking-pricing-link" className="font-bold text-forest underline underline-offset-4 hover:text-forest-leaf">
                                See how we price our quotes
                            </Link>
                        </p>
                        <p className="mt-2 text-xs text-moss">
                            Want a rough number in 30 seconds?{" "}
                            <Link to="/estimate" data-testid="booking-estimate-link" className="font-bold text-forest underline underline-offset-4 hover:text-forest-leaf">
                                Try the instant estimate
                            </Link>
                        </p>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-name">Full name</label>
                            <input id="bk-name" data-testid="booking-form-name-input" required className={inputCls}
                                value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
                        </div>
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-phone">Phone number</label>
                            <input id="bk-phone" data-testid="booking-form-phone-input" required type="tel" className={inputCls}
                                value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" />
                        </div>
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-email">Email</label>
                            <input id="bk-email" data-testid="booking-form-email-input" required type="email" className={inputCls}
                                value={form.email} onChange={set("email")} placeholder="you@example.com" />
                        </div>
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-address">Property address</label>
                            <input id="bk-address" data-testid="booking-form-address-input" required className={inputCls}
                                value={form.address} onChange={set("address")} placeholder="Street address, suburb" />
                        </div>
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-date">Preferred date</label>
                            <input id="bk-date" data-testid="booking-form-date-picker" required type="date"
                                min={new Date().toISOString().split("T")[0]} className={inputCls}
                                value={form.preferred_date} onChange={set("preferred_date")} />
                        </div>
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-time">Preferred time</label>
                            <select id="bk-time" data-testid="booking-form-time-select" className={inputCls}
                                value={form.preferred_time} onChange={set("preferred_time")}>
                                {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>
                    <p className="-mt-4 text-xs text-moss">Preferred date and time are a request only — we'll confirm a suitable time with you.</p>

                    <div>
                        <label className="eyebrow mb-2 block text-forest">How often?</label>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {["One-off", "Weekly", "Fortnightly", "Monthly"].map((f) => {
                                const active = form.frequency === f;
                                return (
                                    <button type="button" key={f} onClick={() => setForm({ ...form, frequency: f })}
                                        data-testid={`frequency-option-${f.toLowerCase().replace(/[^a-z]/g, "")}`} aria-pressed={active}
                                        className={`rounded-2xl border px-4 py-3 text-sm font-bold transition-colors ${
                                            active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                                        }`}>
                                        {f}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-2 text-xs text-moss">
                            Want regular maintenance? Pick weekly, fortnightly or monthly and we&apos;ll set up a recurring schedule with you.
                        </p>
                    </div>

                    {isQuote && (
                        <div>
                            <label className="eyebrow mb-2 block text-forest" htmlFor="bk-budget">What&apos;s your budget? (optional)</label>
                            <input id="bk-budget" data-testid="booking-form-budget-input" className={inputCls}
                                value={form.budget} onChange={set("budget")}
                                placeholder="e.g. around $200 — no pressure, we'll do our best to work with you" />
                        </div>
                    )}

                    <div>
                        <label className="eyebrow mb-2 block text-forest" htmlFor="bk-notes">Additional information</label>
                        <textarea id="bk-notes" data-testid="booking-form-notes-textarea" rows={4} className={inputCls}
                            value={form.notes} onChange={set("notes")}
                            placeholder="Tell us anything helpful — access, size of the job, what needs removing, and so on." />
                    </div>

                    <div>
                        <p className="eyebrow mb-2 text-forest">Photo (optional)</p>
                        <label data-testid="booking-form-photo-upload"
                            className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-forest/30 bg-bone-card px-5 py-4 text-sm font-semibold text-forest transition-colors hover:bg-bone-subtle">
                            <Upload size={16} />
                            {photo ? photo.name : "Upload a photo of the area or items"}
                            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                        </label>
                        {photo && (
                            <div className="relative mt-4 w-40">
                                <img src={photo.data} alt="Upload preview" data-testid="booking-form-photo-preview"
                                    className="aspect-square w-full rounded-xl border border-forest/15 object-cover" />
                                <button type="button" onClick={() => setPhoto(null)} data-testid="booking-form-photo-remove"
                                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-forest text-bone">
                                    <X size={12} />
                                </button>
                            </div>
                        )}
                    </div>

                    <button type="submit" disabled={sending} data-testid="booking-form-submit-button"
                        className="w-full rounded-full bg-forest px-8 py-4 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60 sm:w-auto">
                        {sending ? "Sending..." : isQuote ? "Request Free Quote" : "Submit Booking Request"}
                    </button>
                </form>
            </section>
        </main>
    );
}
